import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";
import { topics } from "@/mocks/topics";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const results =
    keyword.trim().length > 0
      ? topics.filter(
          (item) =>
            item.title.includes(keyword.trim()) ||
            item.tags.some((tag) => tag.includes(keyword.trim())),
        )
      : [];

  const goTopic = (id: string) => {
    setSearchOpen(false);
    setMenuOpen(false);
    setKeyword("");
    navigate(`/topic/${id}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="flex h-16 items-center gap-3 px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-bold text-background-50">
            蝌
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950">{siteInfo.name}</span>
            <span className="mt-0.5 hidden text-[11px] text-foreground-500 sm:block">
              {siteInfo.slogan}
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 xl:flex" aria-label="主导航">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSearchOpen(true);
              setMenuOpen(false);
            }}
            aria-label="搜索事件"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-search-line text-base" aria-hidden="true"></i>
          </button>
          <Link
            to="/contact"
            className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            <i className="ri-megaphone-line text-base" aria-hidden="true"></i>
            提供线索
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="展开导航菜单"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 xl:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 xl:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className="mt-3 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
          >
            <i className="ri-megaphone-line text-base" aria-hidden="true"></i>
            提供线索
          </Link>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground-950/40 px-4 pt-24">
          <div className="w-full max-w-xl rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center gap-2 rounded-md border border-background-300 px-3 py-2">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" aria-hidden="true"></i>
              </span>
              <input
                autoFocus
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入事件关键词或标签，例如：剪辑、辟谣"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setKeyword("");
                }}
                aria-label="关闭搜索"
                className="flex h-6 w-6 cursor-pointer items-center justify-center text-foreground-500 hover:text-foreground-900"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>

            <div className="mt-3 max-h-72 overflow-y-auto">
              {keyword.trim().length === 0 && (
                <p className="px-1 py-6 text-center text-sm text-foreground-500">
                  输入关键词，快速定位正在追踪的事件
                </p>
              )}
              {keyword.trim().length > 0 && results.length === 0 && (
                <p className="px-1 py-6 text-center text-sm text-foreground-500">
                  暂时没有匹配的事件，换个关键词试试
                </p>
              )}
              {results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goTopic(item.id)}
                  className="flex w-full cursor-pointer flex-col gap-1 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="text-sm font-medium text-foreground-900">{item.title}</span>
                  <span className="line-clamp-1 text-xs text-foreground-500">{item.tagline}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}